/**
 * The API client.
 *
 * Keeps Form Lock's best idea — an error type that preserves the HTTP status, so a caller can tell
 * "you are not signed in" from "that does not exist" — and drops its worst: Form Lock's API returns
 * errors in two different shapes, and its frontend carries a function whose whole job is coping
 * with the ambiguity. Vephus's API returns RFC 7807 ProblemDetails and nothing else, so parsing
 * is four lines instead of twenty.
 */

import { getToken, clearSession } from './auth'
import type { Deliveries, LocalModelCatalog, RecommendedCatalog, WeeklyReportState, WeeklyReportSent,
  RoomArea,
  DeveloperMachine,
  Inbox,
  MyAgent,
  MyCredential,
  RoomChatMessage,
  DocDetail,
  DocFolder,
  DocLink,
  DocSearchHit,
  LinkedDoc,
  DocTree,
  DocUploadResult,
  AuthResult,
  Avatar,
  Bug,
  BugComment,
  BugHuntState,
  CreatedInvitation,
  Credential,
  CurrentUser,
  DashboardSummary,
  Developer,
  DeveloperActivity,
  DeveloperDay,
  InvitationPreview,
  InvitationSummary,
  IssuedCredential,
  LiveSession,
  Member,
  OrganizationChoice,
  Paged,
  PromptItem,
  Project,
  RemoteMessage,
  RemoteOverview,
  Provider,
  ReportResult,
  Repository,
  RepositoryLink,
  WorkOrder,
  WorkOrderKind,
  SkillDetail,
  SkillSummary,
  PromptCatalog,
  CatalogPrompt,
  TelegramStatus,
  PlanState,
  SessionFacts,
  PluginItem,
  Teammate,
  RepositorySnapshot,
  RoomScenery,
  SecurityFinding,
  UsageResult,
  SpendLimit,
  SpendBlock,
  SecuritySummary,
  RoomTexts,
  SddOverview,
  SpecDetail,
  SpecsOverview,
  Task,
  Team,
  TelemetrySettings,
  WorkSessionDetail,
  SessionPrompts,
  WorkflowSettings,
  DocSummary,
  PromptQueue,
  QueuedPrompt,
  AgentNote,
  AgentTask,
} from './types'

// Vite fills import.meta.env in the browser; the sandbox runner (scripts/sandbox/runner.ts) loads
// this same module under Node, where only process.env exists.
const ENV: Record<string, string | undefined> =
  import.meta.env ?? (typeof process !== 'undefined' ? (process.env as Record<string, string | undefined>) : {})

const BASE = (ENV.VITE_API_URL ?? '/api/v1').replace(/\/+$/, '')

/** The API's base URL, for the few callers that authenticate with an agent key instead (the desktop runner). */
export const API_BASE = BASE

/**
 * Where the API lives, for configuration shown to people (the MCP server's DEVPULSE_API_URL). The
 * bundle's own origin in development, where Vite proxies to the API on 5199.
 */
export const API_ORIGIN = BASE.startsWith('http')
  ? new URL(BASE).origin
  : typeof window !== 'undefined'
    ? window.location.origin.replace(':5173', ':5199')
    : 'http://localhost:5199'

export class ApiError extends Error {
  readonly status: number
  readonly code: string | undefined

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }

  /** Maps to a localisation key so the message shown is in the reader's language. */
  get translationKey(): string {
    if (this.status === 401) return 'error.unauthorized'
    if (this.status === 403) return 'error.forbidden'
    if (this.status === 404) return 'error.notFound'
    if (this.status === 0) return 'error.network'
    return 'error.generic'
  }
}

interface ProblemDetails {
  title?: string
  detail?: string
  status?: number
  code?: string
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()

  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers as Record<string, string> | undefined),
      },
    })
  } catch (error) {
    // Status 0 means the request never reached a server, which is a different problem from any
    // answer a server could give and deserves a different message.
    throw new ApiError(error instanceof Error ? error.message : 'Network error', 0, 'network')
  }

  if (response.status === 401) {
    // The token is gone or expired. Clearing it here means the next render redirects to sign-in
    // instead of every subsequent request failing the same way.
    clearSession()
    throw new ApiError('Unauthorized', 401, 'unauthorized')
  }

  if (!response.ok) {
    const text = await response.text()
    let problem: ProblemDetails = {}
    try {
      problem = text ? (JSON.parse(text) as ProblemDetails) : {}
    } catch {
      problem = { detail: text.slice(0, 300) }
    }

    throw new ApiError(
      problem.detail ?? problem.title ?? `Request failed (${response.status})`,
      response.status,
      problem.code,
    )
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/**
 * A request whose body or answer is not JSON: an upload, or an image. The browser sets the
 * multipart boundary itself, so no Content-Type is forced here. Errors read as `request`'s do.
 */
async function rawRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken()

  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
  } catch (error) {
    throw new ApiError(error instanceof Error ? error.message : 'Network error', 0, 'network')
  }

  if (!response.ok) {
    let problem: ProblemDetails = {}
    try {
      problem = JSON.parse(await response.text()) as ProblemDetails
    } catch {
      // Not JSON: the status says enough.
    }
    throw new ApiError(problem.detail ?? problem.title ?? `Request failed (${response.status})`, response.status, problem.code)
  }

  return response
}

function asDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('unreadable'))
    reader.readAsDataURL(blob)
  })
}

const get = <T>(path: string): Promise<T> => request<T>(path)
const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
const put = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) })
const del = <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' })
const patch = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) })

/** Builds a query string, omitting anything unset so the server sees absent rather than empty. */
function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, String(value))
  }
  const rendered = search.toString()
  return rendered ? `?${rendered}` : ''
}

export const api = {
  /** Notifications to the phone (lib/push.ts). */
  push: {
    key: () => get<{ publicKey: string | null }>('/push/key'),
    subscribe: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) => post<void>('/push/subscribe', subscription),
    unsubscribe: (endpoint: string) => post<void>('/push/unsubscribe', { endpoint }),
  },
  /** The person's own account: its state, a pause, the end, a copy of the data (docs/refactor/65). */
  account: {
    status: () => get<{ deactivatedAt: string | null; deletionRequestedAt: string | null; deletionDueAt: string | null; soleOwnerOf: string[] }>('/me/account'),
    /** Needs a recent sign-in; the server answers reauth_required otherwise. Every session ends. */
    deactivate: () => post<void>('/me/account/deactivate'),
    /** Always the password or Google. Every session ends; the data goes after the wait. */
    delete: (body: { password?: string; googleIdToken?: string }) => post<{ deletionDueAt: string }>('/me/account/delete', body),
    cancelDeletion: () => post<void>('/me/account/cancel-deletion'),
    /** Everything that is the person's, as one document to keep. */
    export: () => get<unknown>('/me/account/export'),
  },
  auth: {
    signIn: (email: string, password: string) => post<AuthResult>('/auth/sign-in', { email, password }),
    /** acceptTerms: the person ticked the Terms of Use box. The server refuses an account without it. */
    signUp: (organizationName: string, email: string, password: string, displayName: string, acceptTerms: boolean) =>
      post<AuthResult>('/auth/sign-up', { organizationName, email, password, displayName, acceptTerms }),
    me: () => get<CurrentUser>('/auth/me'),
    /** A new token before this one expires, with the same proof of identity (lib/sessionRenewal.ts). */
    refresh: () => post<AuthResult>('/auth/refresh', {}),
    /** A one-time code for opening the browser signed in as this session ("View on the web"). */
    handoff: () => post<{ code: string }>('/auth/handoff'),
    redeemHandoff: (code: string) => post<AuthResult>('/auth/handoff/redeem', { code }),
    /** Every other session ends; this one carries on with the token returned. */
    changePassword: (currentPassword: string, newPassword: string) =>
      post<AuthResult>('/auth/change-password', { currentPassword, newPassword }),
    /** The password again, or Google, for what asks for a recent sign-in. */
    reauthenticate: (body: { password?: string; googleIdToken?: string }) => post<AuthResult>('/auth/reauthenticate', body),
    /** Ends every other session and switches remote control off; this one carries on. */
    endOtherSessions: () => post<AuthResult>('/auth/end-other-sessions'),
    /** passwordRecovery: whether "Esqueci a senha" works here (a server with no mail cannot). */
    config: () => get<{ googleClientId: string | null; passwordRecovery?: boolean }>('/auth/config'),
    /** "Esqueci a senha": the same answer whatever the address (docs/refactor/65). */
    forgotPassword: (email: string) => post<void>('/auth/password/forgot', { email }),
    /** The mailed link's token and the password it sets. */
    resetPassword: (token: string, newPassword: string) => post<void>('/auth/password/reset', { token, newPassword }),
    /** Connects Google to the signed-in account, from Conta (docs/refactor/54): a password account's only way. */
    linkGoogle: (idToken: string) => post<void>('/auth/google/link', { idToken }),
    google: (idToken: string, organizationName?: string, acceptTerms?: boolean) =>
      post<AuthResult>('/auth/google', { idToken, organizationName, acceptTerms }),
    /** Every organization this person belongs to, for the switcher. */
    organizations: () => get<OrganizationChoice[]>('/auth/organizations'),
    /** A new session in another of their organizations, with the role they hold there. */
    switchOrganization: (organizationId: string) => post<AuthResult>('/auth/switch', { organizationId }),
  },

  dashboard: {
    summary: (includeSimulated = true) => get<DashboardSummary>(`/dashboard/summary${query({ includeSimulated })}`),
    live: (includeSimulated = true) => get<LiveSession[]>(`/live${query({ includeSimulated })}`),
  },

  sessions: {
    list: (params: { page?: number; pageSize?: number; developerId?: string; taskId?: string; status?: string; includeSimulated?: boolean }) =>
      get<Paged<LiveSession>>(`/work${query(params)}`),
    detail: (id: string) => get<WorkSessionDetail>(`/work/${id}`),
    /** The prompt that started a session and those sent while it ran — text only for its developer and managers. */
    prompts: (id: string) => get<SessionPrompts>(`/work/${id}/prompts`),
    sdd: (taskId?: string) => get<SddOverview>(`/sdd${query({ taskId })}`),
  },

  bugHunt: {
    state: (params: { teamId?: string; developerId?: string; projectId?: string; includeSimulated?: boolean } = {}) =>
      get<BugHuntState>(`/bug-hunt/state${query(params)}`),
    bugs: (params: { state?: string; search?: string; developerId?: string; page?: number; pageSize?: number } = {}) =>
      get<{ items: Bug[]; total: number; page: number; pageSize: number; counts: BugHuntState['counts'] }>(
        `/bug-hunt/bugs${query(params)}`,
      ),
    register: (body: { externalId: string; title: string; description?: string; priority: string; projectId?: string; assignedDeveloperId?: string; specificationId?: string }) =>
      post<Bug>('/bug-hunt/bugs', body),
    transition: (taskId: string, action: string, developerId?: string, reason?: string) =>
      post<Bug>(`/bug-hunt/bugs/${taskId}/transition`, { action, developerId, reason }),
    /** What was said about a bug, oldest first — including why a tester sent it back. */
    comments: (taskId: string) => get<BugComment[]>(`/bug-hunt/bugs/${taskId}/comments`),
    comment: (taskId: string, text: string) => post<BugComment>(`/bug-hunt/bugs/${taskId}/comments`, { text }),
  },

  tasks: {
    list: (params: { page?: number; pageSize?: number; status?: string; type?: string; projectId?: string; search?: string } = {}) =>
      get<Paged<Task>>(`/tasks${query(params)}`),
    create: (body: Record<string, unknown>) => post<Task>('/tasks', body),
    update: (id: string, body: Record<string, unknown>) => put<Task>(`/tasks/${id}`, body),
  },

  invitations: {
    preview: (token: string) => get<InvitationPreview>(`/invitations/${encodeURIComponent(token)}`),
    accept: (body: { token: string; displayName?: string; password?: string; googleIdToken?: string; acceptTerms: boolean }) =>
      post<AuthResult>('/invitations/accept', body),
  },

  activity: {
    /**
     * `everyone` includes people with no agent running — the room draws everybody. `projectId` (or
     * `projectKey`): only that project's sessions, and only the people with one.
     */
    get: (params: { kind?: string; includeSimulated?: boolean; everyone?: boolean; teamId?: string; developerId?: string; projectId?: string; projectKey?: string } = {}) =>
      get<DeveloperActivity[]>(`/activity${query(params)}`),
  },

  developers: {
    /** One developer's day. `from` and `to` are the reader's local day, as instants. */
    day: (id: string, params: { from: string; to: string; includeSimulated?: boolean }) =>
      get<DeveloperDay>(`/developers/${id}/day${query(params)}`),
    /** Kept prompts: the developer's own, or any for a manager. Empty when the organization keeps none. */
    prompts: (id: string, params: { from: string; to: string; workSessionId?: string }) =>
      get<PromptItem[]>(`/developers/${id}/prompts${query(params)}`),
  },

  specs: {
    list: (params: { projectId?: string; kind?: string; includeSimulated?: boolean } = {}) =>
      get<SpecsOverview>(`/specs${query(params)}`),
    detail: (id: string) => get<SpecDetail>(`/specs/${id}`),
    create: (body: { key: string; title: string; kind: string; projectId?: string; documentPath?: string; description?: string }) =>
      post<SpecDetail>('/specs', body),
    update: (id: string, body: Record<string, unknown>) => put<SpecDetail>(`/specs/${id}`, body),
    requestEstimate: (projectId: string) => post<void>(`/specs/projects/${projectId}/request-estimate`),
  },

  room: {
    /** The room's chat, oldest first. */
    chat: () => get<RoomChatMessage[]>('/room/chat'),
    /** Says it, with a small picture beside it when there is one. The picture lasts a minute. */
    say: (text: string, image?: string) => post<RoomChatMessage>('/room/chat', { text, image }),
    /** The line under my own character's name. */
    status: () => get<{ text: string | null; setAt: string | null }>('/me/status'),
    setStatus: (text: string) => put<{ text: string | null; setAt: string | null }>('/me/status', { text }),
    /** Sends one's own character to an office area (docs/refactor/29). */
    setRoomArea: (area: RoomArea) => put<{ area: RoomArea }>('/me/room-area', { area }),
    // The server answers 204 when the organization kept the defaults; that is null here, never
    // undefined, which a query cache refuses to store.
    texts: async () => (await get<RoomTexts | null>('/room/texts')) ?? null,
    /** Whether the room dresses up for occasions, and the version of its background image. */
    scenery: () => get<RoomScenery>('/room/scenery'),
    /** The organization's background image, as a data URL: an <image> cannot send the token itself. */
    background: async () => asDataUrl(await (await rawRequest('/room/background')).blob()),
    setBackground: async (image: Blob, width: number, height: number) => {
      const form = new FormData()
      form.append('file', image, 'background.jpg')
      form.append('width', String(width))
      form.append('height', String(height))
      return (await (await rawRequest('/room/background', { method: 'PUT', body: form })).json()) as RoomScenery
    },
    removeBackground: () => del<void>('/room/background'),
    /** The team's board (docs/refactor/25), as a data URL. A GIF stays a GIF: it is never re-encoded. */
    board: async () => asDataUrl(await (await rawRequest('/room/board')).blob()),
    setBoard: async (file: Blob) => {
      const form = new FormData()
      form.append('file', file, 'board')
      return (await (await rawRequest('/room/board', { method: 'PUT', body: form })).json()) as RoomScenery
    },
    removeBoard: () => del<void>('/room/board'),
  },

  docs: {
    tree: () => get<DocTree>('/docs/tree'),
    get: (id: string) => get<DocDetail>(`/docs/${id}`),
    byPath: (path: string) => get<DocDetail>(`/docs/by-path${query({ path })}`),
    /** Writes a document by its path, creating folders as needed. */
    upsertByPath: (path: string, content: string) => put<DocDetail>('/docs/by-path', { path, content }),
    search: (q: string) => get<DocSearchHit[]>(`/docs/search${query({ q })}`),
    linked: (params: { taskId?: string; specificationId?: string }) => get<LinkedDoc[]>(`/docs/linked${query(params)}`),
    create: (body: { folderId: string | null; name: string; content: string }) => post<DocDetail>('/docs', body),
    /** With expectedVersion, refused when somebody saved in between. */
    update: (id: string, body: { content: string; expectedVersion: number }) => put<DocDetail>(`/docs/${id}`, body),
    move: (id: string, body: { folderId: string | null; name?: string; moveToRoot?: boolean }) => post<DocDetail>(`/docs/${id}/move`, body),
    remove: (id: string) => del<void>(`/docs/${id}`),
    createFolder: (body: { name: string; parentId: string | null }) => post<DocFolder>('/docs/folders', body),
    updateFolder: (id: string, body: { name?: string; parentId?: string | null; moveToRoot?: boolean }) => put<void>(`/docs/folders/${id}`, body),
    removeFolder: (id: string) => del<void>(`/docs/folders/${id}`),
    /** Pinned instructions: what goes before the prompt at the start of every agent session. */
    pinned: () => get<DocSummary[]>('/docs/pinned'),
    /** The pinned documents as one block, as an agent reads them before a conversation's first prompt. */
    pinnedText: () => get<{ text: string }>('/docs/pinned/text'),
    setPinned: (id: string, pinned: boolean) => put<void>(`/docs/${id}/pinned`, { pinned }),
    /** Markdown files, each with its path relative to `folder` — a whole folder keeps its subfolders. */
    upload: async (files: Array<{ file: File; path: string }>, folder: string | null) => {
      const form = new FormData()
      for (const { file, path } of files) {
        form.append('files', file, file.name)
        form.append('paths', path)
      }
      if (folder) form.append('folder', folder)
      return (await (await rawRequest('/docs/upload', { method: 'POST', body: form })).json()) as DocUploadResult[]
    },
    /** The Markdown as a file, through the API so the token goes with it. */
    download: async (id: string) => (await rawRequest(`/docs/${id}/raw`)).blob(),
    link: (id: string, body: { taskId?: string; taskExternalId?: string; specificationId?: string; specificationKey?: string }) =>
      post<DocLink>(`/docs/${id}/links`, body),
    unlink: (id: string, linkId: string) => del<void>(`/docs/${id}/links/${linkId}`),
  },

  me: {
    /** The signed-in person's own agent keys. */
    credentials: () => get<MyCredential[]>('/me/credentials'),
    /** "Ver minhas respostas na web": keep the person's own agents' answers for them alone, a week. */
    keepReplies: (keep: boolean) => put<void>('/me/replies', { keep }),
    /** "Memória da IA" and sharing it with the project's AIs (docs/refactor/17). */
    memory: (body: { aiMemory?: boolean; shareMemory?: boolean }) => put<void>('/me/memory', body),
    /** How this person's AI agents look in the office (docs/refactor/25): the preference as JSON; null is random. */
    agentLooks: (preference: string | null) => put<void>('/me/agent-looks', { preference }),
    issueCredential: (body: { name?: string; machineLabel?: string; expiresInDays?: number }) =>
      post<{ id: string; name: string; key: string; keyPrefix: string; expiresAt?: string | null }>('/me/credentials', body),
    revokeCredential: (id: string) => post<void>(`/me/credentials/${id}/revoke`),
    /** "Testar uma chave" (docs/refactor/26): what Vephus says of a pasted agent key. Checked, not kept. */
    checkKey: (key: string) => post<{ state: 'accepted' | 'revoked' | 'expired' | 'other_organization' | 'unknown' | 'malformed'; mine: boolean; name?: string | null; developer?: string | null; createdAt?: string | null; lastUsedAt?: string | null; expiresAt?: string | null; revokedReason?: string | null; credentialId?: string | null }>('/me/key-check', { key }),

    /** The person's own agents: characters in the room, each with its own key. */
    agents: () => get<MyAgent[]>('/me/agents'),
    createAgent: (name: string) =>
      post<{ id: string; name: string; key: string; keyPrefix: string; expiresAt?: string | null }>('/me/agents', { name }),
    issueAgentKey: (developerId: string) =>
      post<{ id: string; name: string; key: string; keyPrefix: string; expiresAt?: string | null }>(`/me/agents/${developerId}/keys`, {}),
    retireAgent: (developerId: string) => post<void>(`/me/agents/${developerId}/retire`),

    /** Accepts the Terms of Use in force, for somebody signed in who has not yet. */
    acceptTerms: () => post<{ currentVersion: string; accepted: boolean }>('/me/terms/accept'),
  },

  /** The manager's view: a specification's sessions, or a day's (the Team plan). */
  deliveries: {
    get: (params: { specificationId?: string; day?: string; offsetMinutes?: number }) => get<Deliveries>(`/deliveries${query(params)}`),
  },

  billing: {
    state: () => get<PlanState>('/billing'),
    checkout: (plan: string, seats?: number) => post<{ url: string }>('/billing/checkout', { plan, seats }),
    /** The one Team trial: fourteen days with no card (docs/refactor/41 §4.3). */
    trial: () => post<PlanState>('/billing/trial'),
    portal: () => post<{ url: string }>('/billing/portal'),
  },

  analysis: {
    facts: (sessionId: string) => get<SessionFacts>(`/sessions/${sessionId}/facts`),
    ask: (sessionId: string, question: string) => post<WorkOrder>(`/sessions/${sessionId}/ask`, { question }),
    reviewPrompt: (body: { prompt: string; repositoryId?: string | null; reviewerDeveloperId?: string | null }) => post<WorkOrder>('/prompts/review', body),
  },

  plugins: {
    list: () => get<PluginItem[]>('/plugins'),
    publish: (manifest: unknown) => post<PluginItem>('/plugins', manifest),
    create: (body: { name: string; description: string; repositoryId?: string | null }) => post<WorkOrder>('/plugins/create', body),
    install: (id: string) => post<void>(`/plugins/${id}/install`),
    uninstall: (id: string) => del<void>(`/plugins/${id}/install`),
    remove: (id: string) => del<void>(`/plugins/${id}`),
  },

  contact: {
    send: (body: { name: string; email: string; message: string; source: 'web' | 'desktop'; website?: string }) => post<void>('/contact', body),
  },

  telegram: {
    status: () => get<TelegramStatus>('/telegram'),
    /** The t.me link that opens the bot with a one-time code. */
    link: () => post<{ url: string }>('/telegram/link'),
    unlink: () => del<void>('/telegram/link'),
  },

  prompts: {
    list: () => get<PromptCatalog>('/prompts'),
    save: (key: string, body: string) => put<CatalogPrompt>(`/prompts/${encodeURIComponent(key)}`, { body }),
    reset: (key: string) => del<CatalogPrompt>(`/prompts/${encodeURIComponent(key)}`),
    /** Which release the organization sends; null follows the newest its plan includes. */
    chooseVersion: (key: string, version: string | null) => put<CatalogPrompt>(`/prompts/${encodeURIComponent(key)}/version`, { version }),
  },

  /** The free local AI's model catalog (SDD ia-local-gratuita): honest statuses from real probes. */
  localModels: () => get<LocalModelCatalog>('/local-models'),

  /** The product's current version, for the desktop app to compare with its own on open. */
  app: {
    version: () => get<{ version: string; downloads: string }>('/app/version'),
  },

  /** The curated catalog behind "Recomendados" in Skills e MCPs (docs/refactor/42). */
  recommended: () => get<RecommendedCatalog>('/recommended'),

  skills: {
    list: () => get<SkillSummary[]>('/skills'),
    get: (id: string) => get<SkillDetail>(`/skills/${id}`),
    upload: (body: { fileName: string; content: string }) => post<SkillDetail>('/skills/upload', body),
    /** Sends your own agent to fetch it from git; it saves it back when done. */
    importGit: (body: { url: string; repositoryId?: string | null }) => post<WorkOrder>('/skills/import', body),
    install: (id: string, repositoryId?: string | null) => post<WorkOrder>(`/skills/${id}/install`, { repositoryId: repositoryId ?? null }),
    remove: (id: string) => del<void>(`/skills/${id}`),
  },

  /** Tasks with several agents (docs/refactor/15): the person opens one; the agents work it through the MCP. */
  agentTasks: {
    /** Every note between the person's agents, oldest first (docs/refactor/15). */
    notes: () => get<AgentNote[]>('/agent-tasks/notes'),
    /** The orchestrator's prompt comes back: the request inside its role, from the server's catalog. */
    create: (body: { text: string; repositoryId: string | null; agents: number; machine?: string | null; teammates?: Array<{ agent: number; developerId: string }> }) =>
      post<{ id: string; prompt: string; agents: number; machine?: string | null }>('/agent-tasks', body),
    list: () => get<AgentTask[]>('/agent-tasks'),
    get: (id: string) => get<AgentTask>(`/agent-tasks/${id}`),
  },

  workOrders: {
    /** The signed-in developer's own orders. Nobody else's: the server decides whose. */
    list: () => get<WorkOrder[]>('/work-orders'),
    create: (body: {
      kind: WorkOrderKind
      instructions: string
      title?: string | null
      repositoryId?: string | null
      docPath?: string | null
      maxIterations?: number | null
      intervalMinutes?: number | null
      /** "Num horário": a once / until-done order that starts at this time instead of now. */
      startAt?: string | null
      builderDeveloperId?: string | null
      reviewerDeveloperId?: string | null
      /** "Quem faz?" (components/WhoDoesIt.tsx): one of the person's own computers, an agent there, or a listening session. */
      machine?: string | null
      agent?: string | null
      listener?: string | null
      /** "Repetir em horários" (docs/refactor/66): clock times instead of an interval, read in timeZone. */
      cron?: string | null
      timeZone?: string | null
    }) => post<WorkOrder>('/work-orders', body),
    /** Managing a loop (docs/refactor/66): pause, resume, run once now, edit. */
    pause: (id: string) => post<WorkOrder>(`/work-orders/${id}/pause`),
    resume: (id: string) => post<WorkOrder>(`/work-orders/${id}/resume`),
    runNow: (id: string) => post<WorkOrder>(`/work-orders/${id}/run-now`),
    update: (id: string, body: { instructions?: string | null; title?: string | null; intervalMinutes?: number | null; cron?: string | null; timeZone?: string | null }) =>
      patch<WorkOrder>(`/work-orders/${id}`, body),
    /** The next three runs of a cron, or the code saying why it is refused — before saving. */
    previewSchedule: (cron: string, timeZone: string) =>
      post<{ next: string[]; error: string | null }>('/work-orders/schedule-preview', { cron, timeZone }),
    /** Who a team task can go to, and whether each can now. */
    /** Who can take team work — with a repository, only its team's members. */
    teammates: (repositoryId?: string | null) => get<Teammate[]>(`/work-orders/teammates${repositoryId ? `?repositoryId=${repositoryId}` : ''}`),
    stop: (id: string) => post<void>(`/work-orders/${id}/stop`),
  },

  repositories: {
    list: () => get<RepositoryLink[]>('/repositories'),
    /** The free project, "Livre" (docs/refactor/49), made when the organization has none. */
    ensureDefault: () => post<void>('/repositories/default', {}),
    link: (body: { remoteUrl?: string | null; name?: string | null; defaultBranch?: string | null; projectId?: string | null; teamId?: string | null }) =>
      post<RepositoryLink>('/repositories', body),
    /** Unlinks it; the work orders still running in it stop, and the answer says how many. */
    unlink: (id: string) => del<{ found: boolean; stoppedOrders: number }>(`/repositories/${id}`),
    /** Moves it to another project or team. */
    update: (id: string, body: { projectId?: string | null; teamId?: string | null }) => patch<RepositoryLink>(`/repositories/${id}`, body),
    /** The projects a repository can belong to. */
    projects: () => get<Array<{ id: string; name: string; key: string }>>('/repositories/projects'),
    snapshot: (id: string) => get<RepositorySnapshot | null>(`/repositories/${id}/snapshot`),
    /** The teams a repository can belong to, for whoever links one. */
    teams: () => get<Array<{ id: string; name: string }>>('/repositories/teams'),
    saveSnapshot: (id: string, body: { branch: string | null; head: string | null; files: string[] }) => put<void>(`/repositories/${id}/snapshot`, body),
  },

  remote: {
    /** The signed-in developer's own agents and thread. Nobody else's: the server decides whose. */
    overview: () => get<RemoteOverview>('/remote'),
    setEnabled: (enabled: boolean, hours?: number) => put<{ until?: string | null }>('/remote/settings', { enabled, hours }),
    send: (text: string, clientSessionId: string | null) => post<RemoteMessage>('/remote/messages', { text, clientSessionId }),
    cancel: (id: string) => del<void>(`/remote/messages/${id}`),
    /** Whether teammates may send your agents steps of team work. */
    setTeam: (accept: boolean) => put<void>('/remote/team', { accept }),
  },

  /** Prompts for agents with no remote control of their own: the desktop app runs them. */
  /** What waits for the person: work that ended, needs them, failed, or waits for their computer. */
  inbox: {
    list: () => get<Inbox>('/inbox'),
    read: (keys: string[]) => post<void>('/inbox/read', { keys }),
    archive: (keys: string[]) => post<void>('/inbox/archive', { keys }),
    /** Answers an agent that stopped to ask (docs/refactor/51, §6.4): "Aprovar", or the person's words. */
    answer: (key: string, body: { approve: true } | { approve: false; text: string }) =>
      post<QueuedPrompt>('/inbox/answer', { key, text: body.approve ? null : body.text, approve: body.approve }),
    /** "Aceitar" or "Recusar" an invitation to a team or a project (docs/refactor/55); the key is "invite:{id}". */
    answerInvite: (key: string, accept: boolean) => post<void>(`/inbox/invites/${key.replace(/^invite:/, '')}/${accept ? 'accept' : 'decline'}`),
  },

  promptQueue: {
    list: () => get<PromptQueue>('/prompt-queue'),
    /** `lane`: which of the person's parallel agents runs it (1, the default; 2, 3… from the prompt box's "+"). */
    send: (text: string, repositoryId: string | null, lane?: number, machine?: string, agent?: string) =>
      post<QueuedPrompt>('/prompt-queue', { text, repositoryId, ...(lane && lane > 1 ? { lane } : {}), ...(machine ? { machine } : {}), ...(agent ? { agent } : {}) }),
    /** The person's computers with the desktop app: on or not, projects open, what each runs. */
    machines: () => get<DeveloperMachine[]>('/prompt-queue/machines'),
    /** Makes this computer the person's principal (docs/refactor/23), or stops it being so. */
    principal: (machine: string, principal: boolean) => put<void>(`/prompt-queue/machines/${encodeURIComponent(machine)}/principal`, { principal }),
    cancel: (id: string) => del<void>(`/prompt-queue/${id}`),
    /** "Desfazer este turno" from anywhere: the computer that ran it puts its folder back (docs/refactor/02). */
    undo: (id: string) => post<QueuedPrompt>(`/prompt-queue/${id}/undo`, {}),
  },

  security: {
    findings: (params: { from?: string; to?: string; severity?: string; developerId?: string; unreviewed?: boolean } = {}) =>
      get<SecurityFinding[]>(`/security/findings${query(params)}`),
    summary: () => get<SecuritySummary>('/security/summary'),
    review: (id: string) => post<void>(`/security/findings/${id}/review`),
    /** The signed-in developer's own findings. */
    mine: (params: { from: string; to: string }) => get<SecurityFinding[]>(`/security/mine${query(params)}`),
  },

  usage: {
    get: (params: { from?: string; to?: string; teamId?: string; includeSimulated?: boolean } = {}) =>
      get<UsageResult>(`/usage${query(params)}`),
    /** Spending limits (docs/refactor/51, §6.3), each with what its scope spent this day or month. */
    limits: () => get<SpendLimit[]>('/usage/limits'),
    createLimit: (body: { scope: SpendLimit['scope']; targetId?: string | null; targetKey?: string | null; period: SpendLimit['period']; alertUsd?: number | null; stopUsd?: number | null }) =>
      post<SpendLimit>('/usage/limits', body),
    deleteLimit: (id: string) => del<void>(`/usage/limits/${id}`),
    /** Whether a spending limit stops new work here; null when nothing does. */
    spendCheck: async (params: { repositoryId?: string | null; agent?: string | null }): Promise<SpendBlock | null> =>
      (await get<SpendBlock | undefined>(`/prompt-queue/spend-check${query({ repositoryId: params.repositoryId ?? undefined, agent: params.agent ?? undefined })}`)) ?? null,
  },

  reports: {
    run: (params: Record<string, string | number | boolean | undefined>) =>
      get<ReportResult>(`/reports${query(params)}`),
  },

  settings: {
    organization: () => get<{ id: string; name: string; slug: string; isDemo: boolean; createdAt: string }>('/settings/organization'),
    rename: (name: string) => put('/settings/organization', { name }),

    /** The manager's week by email (docs/refactor/41 §4, feature 7): the switch, and "Enviar agora". */
    weeklyReport: () => get<WeeklyReportState>('/settings/weekly-report'),
    setWeeklyReport: (enabled: boolean) => put<WeeklyReportState>('/settings/weekly-report', { enabled }),
    sendWeeklyReport: () => post<WeeklyReportSent>('/settings/weekly-report/send'),

    teams: () => get<Team[]>('/settings/teams'),
    createTeam: (body: { name: string; description?: string; kind?: 'Team' | 'Squad'; parentTeamId?: string | null }) =>
      post<Team>('/settings/teams', body),
    /** `parentTeamId` as an empty GUID makes a squad stand alone. */
    updateTeam: (id: string, body: { name?: string; description?: string; parentTeamId?: string }) =>
      put<void>(`/settings/teams/${id}`, body),
    deleteTeam: (id: string) => del<void>(`/settings/teams/${id}`),
    /** "invited": somebody else joins when they accept it in their inbox (docs/refactor/55). */
    addMember: (teamId: string, developerId: string) => post<{ status: 'added' | 'invited' }>(`/settings/teams/${teamId}/members/${developerId}`),
    removeMember: (teamId: string, developerId: string) => del<void>(`/settings/teams/${teamId}/members/${developerId}`),

    developers: () => get<Developer[]>('/settings/developers'),
    createDeveloper: (body: Record<string, unknown>) => post<Developer>('/settings/developers', body),
    updateDeveloper: (id: string, body: Record<string, unknown>) => put<Developer>(`/settings/developers/${id}`, body),
    /** Null when the person never chose one. */
    getAvatar: async (developerId: string) => (await get<Avatar | null>(`/settings/developers/${developerId}/avatar`)) ?? null,
    setAvatar: (id: string, body: Avatar) => put(`/settings/developers/${id}/avatar`, body),

    projects: () => get<Project[]>('/settings/projects'),
    createProject: (body: Record<string, unknown>) => post<Project>('/settings/projects', body),
    /** Renames, archives, or puts a project on a team. An empty GUID as teamId unlinks it. */
    updateProject: (id: string, body: Record<string, unknown>) => put<Project>(`/settings/projects/${id}`, body),

    repositories: () => get<Repository[]>('/settings/repositories'),
    createRepository: (body: Record<string, unknown>) => post<Repository>('/settings/repositories', body),

    providers: () => get<Provider[]>('/settings/providers'),
    updateModel: (id: string, body: Record<string, unknown>) => put(`/settings/providers/models/${id}`, body),

    members: () => get<Member[]>('/settings/members'),

    invitations: () => get<InvitationSummary[]>('/settings/members/invitations'),
    createInvitation: (body: { email: string; role: string; expiresInDays?: number }) =>
      post<CreatedInvitation>('/settings/members/invitations', body),
    revokeInvitation: (id: string) => del<void>(`/settings/members/invitations/${id}`),
    invite: (body: Record<string, unknown>) => post<Member>('/settings/members', body),
    setRole: (userId: string, role: string) => put<void>(`/settings/members/${userId}/role`, { role }),
    /** Kids mode for a member (docs/refactor/48): the adult's switch. */
    setKidsMode: (userId: string, enabled: boolean) => put<void>(`/settings/members/${userId}/kids`, { enabled }),

    credentials: () => get<Credential[]>('/settings/credentials'),
    issueCredential: (body: Record<string, unknown>) => post<IssuedCredential>('/settings/credentials', body),
    revokeCredential: (id: string, reason?: string) => post<void>(`/settings/credentials/${id}/revoke`, { reason }),
    rotateCredential: (id: string) => post<IssuedCredential>(`/settings/credentials/${id}/rotate`),

    telemetry: () => get<TelemetrySettings>('/settings/telemetry'),
    saveTelemetry: (body: TelemetrySettings) => put<TelemetrySettings>('/settings/telemetry', body),

    workflow: () => get<WorkflowSettings>('/settings/workflow'),
    saveWorkflow: (body: WorkflowSettings) => put<WorkflowSettings>('/settings/workflow', body),
  },
}
